import _ from 'lodash'
import { v4 as uuidv4 } from 'uuid'
import { IDB_taskReportRead } from '../IDB/IDB.table.taskReport'
import { Rpts_saveTaskReportData } from '../Rpts/Rpts.saveTaskReportData'
import { Unit_toDisplayUnit } from './Unit.toDisplayUnit'
import {
    IDB_routedUnitsDelete,
    IDB_routedUnitsReadAll,
    IDB_routedUnitsWrite,
    IDB_routedUnitWrite,
} from '../IDB/IDB.table.routedUnit'
import {
    type Type_DisplayUnitUuid,
    type Type_IDisplayUnit,
    type Type_IReportSection,
    type Type_IRoutedUnitsSet,
    type Type_ITaskReport,
    type Type_IUnit,
    Type_toDisplayUnitUuid,
} from '../Type/Type.hive'

/*
These functions handle 'unit-routing'. This is where the process flow in one activity is able to add units to another
activity (eg products marked out-of-stock are added to an ordering activity. )
*/

export async function Unit_moveUnitsToActivity(
    unitIds: number[],
    origin: number,
    destination: number,
    sectionName: string,
    deps: {
        IDB_routedUnitsReadAll: typeof IDB_routedUnitsReadAll
        IDB_routedUnitsWrite: typeof IDB_routedUnitsWrite
    } = { IDB_routedUnitsReadAll, IDB_routedUnitsWrite }
) {
    const units = unitIds.map((id) => {
        return { id }
    })

    const entries = await deps.IDB_routedUnitsReadAll()
    const existingRoutedUnits = entries.find(
        (item) => item.originActivity === origin && item.destinationActivity === destination
    )

    if (!existingRoutedUnits) {
        await deps.IDB_routedUnitsWrite([
            {
                originActivity: origin,
                destinationActivity: destination,
                section: [
                    {
                        name: sectionName,
                        units,
                    },
                ],
            },
        ])
    } else {
        const existingSection = existingRoutedUnits.section.find((section) => section.name === sectionName)

        if (existingSection) {
            existingSection.units = _.chain(existingSection.units).concat(units).uniqBy('id').value()
        } else {
            existingRoutedUnits.section.push({
                name: sectionName,
                units,
            })
        }

        await deps.IDB_routedUnitsWrite([existingRoutedUnits])
    }
}

export function Unit_addRoutedUnitsToActivity(
    targetActivity: number,
    sections: Type_IReportSection[],
    displayUnits: Type_IDisplayUnit[],
    unitLookup: Type_IUnit[],
    routedUnitSets: Type_IRoutedUnitsSet[]
): { sections: Type_IReportSection[]; displayUnits: Type_IDisplayUnit[] } {
    const routedSections = routedUnitSets
        .filter((routedUnitSet) => routedUnitSet.destinationActivity === targetActivity)
        .reduce((acc, routedUnitSet) => acc.concat(routedUnitSet.section), [] as Type_IRoutedUnitsSet['section'])

    return routedSections.reduce(
        ({ sections, displayUnits }, routedSection) => {
            const newDisplayUnits = [] as Type_IDisplayUnit[]
            const sectionProducts = [] as Type_DisplayUnitUuid[]

            routedSection.units.forEach((unitToAdd) => {
                const existingDisplayUnit = displayUnits.find((displayUnit) => displayUnit.unit.id === unitToAdd.id)
                if (existingDisplayUnit) return

                const unit = unitLookup.find((u) => u.id === unitToAdd.id)
                if (!unit) return

                const newDisplayUnit = Unit_toDisplayUnit(Type_toDisplayUnitUuid(uuidv4()), unit, unitToAdd, null)
                newDisplayUnits.push(newDisplayUnit)
                sectionProducts.push(newDisplayUnit.instanceId)
            })

            if (sectionProducts.length === 0) return { sections, displayUnits }

            const existingSection = sections.find((section) => section.name === routedSection.name)
            if (!existingSection) {
                const newSection: Type_IReportSection = {
                    label: routedSection.name,
                    name: routedSection.name,
                    sectionProducts,
                    info: '',
                    addUnits: false,
                    collapse: false,
                    type: 'ready',
                }

                return {
                    sections: [...sections, newSection],
                    displayUnits: [...displayUnits, ...newDisplayUnits],
                }
            } else {
                sections = sections.map((section) => {
                    if (section.name === existingSection.name) {
                        return {
                            ...section,
                            sectionProducts: [...section.sectionProducts, ...sectionProducts],
                        }
                    }
                    return section
                })
                return {
                    sections,
                    displayUnits: [...displayUnits, ...newDisplayUnits],
                }
            }
        },
        {
            sections,
            displayUnits,
        }
    )
}

export async function Unit_deleteAllRoutedUnits() {
    const entries = await IDB_routedUnitsReadAll().catch<Type_IRoutedUnitsSet[]>(() => [])
    entries.forEach(async (routedUnit) => {
        await IDB_routedUnitsDelete([routedUnit.id]).catch<Type_IRoutedUnitsSet>(() => null)
    })
}

export async function Unit_removeRoutedUnits(originActivity: number, unitsToRemove: number[]) {
    let entries = await IDB_routedUnitsReadAll().catch<Type_IRoutedUnitsSet[]>(() => [])
    entries = entries.filter((routedUnit) => routedUnit.originActivity === originActivity)

    entries.forEach(async (routedUnit) => {
        routedUnit.section = routedUnit.section.map((section) => {
            section.units = _.chain(section.units)
                .filter((unit) => !unitsToRemove.includes(unit.id))
                .value()
            return section
        })

        await IDB_routedUnitWrite(routedUnit)
        const task = await IDB_taskReportRead(routedUnit.destinationActivity).catch<Type_ITaskReport>(() => null)

        if (task && task.report.type === 'report') {
            task.report.units = task.report.units.filter((unit) => !unitsToRemove.includes(unit.unit.id))
            Rpts_saveTaskReportData(task.id, task.report)
        }
    })
}
